import type { RiskSignalGroupKind } from '@onefootprint/request-types/dashboard';

type SignalFamily =
  | 'selfie'
  | 'matchesInput'
  | 'consistency'
  | 'authenticity'
  | 'validity'
  | 'identity'
  | 'name'
  | 'dob'
  | 'ssn'
  | 'address'
  | 'phone'
  | 'email'
  | 'businessName'
  | 'dba'
  | 'businessAddress'
  | 'tin'
  | 'registration'
  | 'beneficialOwners'
  | 'websitePhone'
  | 'other';

const KYC_IDENTITY_CODES = new Set<string>([
  'id_not_located',
  'id_flagged',
  'thin_file',
  'multiple_records_found',
  'subject_deceased',
  'bureau_deleted_record',
  'credit_more_than3_inquiries_in_last30_days',
  'credit_established_before_ssn_date',
]);

const DOC_AUTHENTICITY_PARTS = [
  'tamper',
  'alteration',
  'fake',
  'digital_fraud',
  'screen_capture',
  'paper_capture',
  'visible_photo_features',
  'live_capture',
  'alignment',
  'collected_via_desktop',
];

const DOC_VALIDITY_CODES = new Set<string>([
  'document_verified',
  'document_not_verified',
  'document_upload_failed',
  'document_ocr_successful',
  'document_ocr_not_successful',
]);

const DOC_VALIDITY_PARTS = ['expire', 'expiration', 'classify', 'type_', 'country_code', 'permit', 'age_less_than'];

const startsWithAny = (code: string, prefixes: string[]) => prefixes.some(prefix => code.startsWith(prefix));
const includesAny = (code: string, parts: string[]) => parts.some(part => code.includes(part));

// Order matters: crosscheck and check-digit codes mention expiration too, so consistency wins before validity.
const getDocFamily = (code: string): SignalFamily => {
  if (code.startsWith('document_selfie_')) {
    return 'selfie';
  }
  if (code.startsWith('document_ocr_') && code.includes('_match')) {
    return 'matchesInput';
  }
  if (
    includesAny(code, ['crosscheck', 'check_digit']) ||
    startsWithAny(code, ['document_barcode_', 'document_pdf417_', 'document_mrz_', 'document_qr_code'])
  ) {
    return 'consistency';
  }
  if (includesAny(code, DOC_AUTHENTICITY_PARTS)) {
    return 'authenticity';
  }
  if (DOC_VALIDITY_CODES.has(code) || code.startsWith('curp_') || includesAny(code, DOC_VALIDITY_PARTS)) {
    return 'validity';
  }
  return 'other';
};

const getKycFamily = (code: string): SignalFamily => {
  if (KYC_IDENTITY_CODES.has(code)) {
    return 'identity';
  }
  if (code.startsWith('name_')) {
    return 'name';
  }
  if (code.startsWith('dob_')) {
    return 'dob';
  }
  if (startsWithAny(code, ['ssn_', 'itin_', 'us_tax_id_'])) {
    return 'ssn';
  }
  if (code.startsWith('address_')) {
    return 'address';
  }
  if (startsWithAny(code, ['phone_', 'input_phone_'])) {
    return 'phone';
  }
  if (code.startsWith('email_')) {
    return 'email';
  }
  return 'other';
};

const getKybFamily = (code: string): SignalFamily => {
  if (code.startsWith('business_name_')) {
    return 'businessName';
  }
  if (code.startsWith('business_dba_')) {
    return 'dba';
  }
  if (code.startsWith('business_address_')) {
    return 'businessAddress';
  }
  if (code.startsWith('tin_')) {
    return 'tin';
  }
  if (code.startsWith('sos_')) {
    return 'registration';
  }
  if (code.startsWith('beneficial_owner')) {
    return 'beneficialOwners';
  }
  if (startsWithAny(code, ['business_website_', 'business_phone_number_'])) {
    return 'websitePhone';
  }
  return 'other';
};

/** The analyst-facing family a signal belongs to, or undefined for groups short enough to stay one list. */
const getSignalFamily = (group: RiskSignalGroupKind, reasonCode: string): SignalFamily | undefined => {
  if (group === 'doc') {
    return getDocFamily(reasonCode);
  }
  if (group === 'kyc') {
    return getKycFamily(reasonCode);
  }
  if (group === 'kyb') {
    return getKybFamily(reasonCode);
  }
  return undefined;
};

export type { SignalFamily };
export default getSignalFamily;
